import { MutableRefObject, useEffect, memo } from 'react';
import { Gallery, Item, useGallery } from 'react-photoswipe-gallery';

import { Button } from './styles';

interface PropsNewImageGallery {
  images: string[];
  currentImage: string;
  alt?: string;
  maxHeight?: string;
  open?: MutableRefObject<() => void | null>;
}

const DEFAULT_IMAGE_DIMENSIONS = {
  width: 1000,
  height: 1000,
} as const;

const BASE_IMAGE_STYLES = {
  cursor: 'pointer' as const,
  maxWidth: '100%' as const,
} as const;

const GALLERY_OPTIONS = {
  bgOpacity: 0.8,
  showHideOpacity: true,
  getThumbBoundsFn: false,
} as const;

const GalleryItem = memo(
  ({
    image,
    currentImage,
    maxHeight = '100px',
    alt,
  }: {
    image: string;
    currentImage: string;
    maxHeight?: string;
    alt?: string;
  }) => {
    const isVisible = image === currentImage;
    
    const imageStyles = {
      ...BASE_IMAGE_STYLES,
      maxHeight,
    };

    return (
      <Item key={image} original={image} {...DEFAULT_IMAGE_DIMENSIONS}>
        {({ ref, open }) => (
          <div 
            className="image-gallery" 
            style={{ visibility: isVisible ? 'visible' : 'hidden' }}
          >
            <Button type="button" onClick={open}>
              <img
                style={imageStyles}
                src={image}
                ref={ref as React.MutableRefObject<HTMLImageElement>}
                alt={alt}
                loading="lazy"
              />
            </Button>
          </div>
        )}
      </Item>
    );
  },
);

function GalleryContent({
  images,
  currentImage,
  maxHeight = '100px',
  alt,
  open,
}: PropsNewImageGallery) {
  const { open: openGallery } = useGallery();

  useEffect(() => {
    if (open) {
      open.current = openGallery;
    }
  }, [open, openGallery]);

  return (
    <>
      {images.map((image) => (
        <GalleryItem
          key={image}
          image={image}
          currentImage={currentImage}
          maxHeight={maxHeight}
          alt={alt}
        />
      ))}
    </>
  );
}

function NewImageGallery(props: PropsNewImageGallery) {
  return (
    <Gallery
      id="new-image-gallery"
      shareButton={false}
      fullscreenButton={false}
      zoomButton={false}
      options={GALLERY_OPTIONS}
    >
      <GalleryContent {...props} />
    </Gallery>
  );
}

export default memo(NewImageGallery);
